import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Car, DollarSign, TrendingUp, Package, ExternalLink, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../lib/api';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentVehicles, setRecentVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const copyCatalogueLink = () => {
    const base = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    const url = `${base}/catalogue`;
    navigator.clipboard.writeText(url);
    toast.success('Lien du catalogue copié !');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoadError(false);
    const results = await Promise.allSettled([
      axios.get(`${API}/stats`, { timeout: 20000 }),
      axios.get(`${API}/vehicles?limit=5`, { timeout: 20000 })
    ]);
    const [statsRes, vehiclesRes] = results;

    let anySuccess = false;
    if (statsRes.status === 'fulfilled') {
      setStats(statsRes.value.data);
      anySuccess = true;
    } else {
      console.error('Stats fetch failed:', statsRes.reason);
    }
    if (vehiclesRes.status === 'fulfilled') {
      setRecentVehicles((vehiclesRes.value.data || []).slice(0, 5));
      anySuccess = true;
    } else {
      console.error('Vehicles fetch failed:', vehiclesRes.reason);
    }

    if (!anySuccess) {
      setLoadError(true);
      toast.error('Impossible de charger le tableau de bord');
    }
    setLoading(false);
  };

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  if (loadError) {
    return (
      <div className="text-center py-16" data-testid="dashboard-error-view">
        <div className="text-5xl mb-4">⚠️</div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Impossible de charger le tableau de bord</h2>
        <p className="text-slate-600 mb-6">Vérifiez votre connexion et réessayez.</p>
        <button
          onClick={() => { setLoading(true); fetchData(); }}
          data-testid="dashboard-retry-btn"
          className="px-6 py-3 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors font-semibold"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total véhicules',
      value: stats?.total_vehicles || 0,
      icon: Car,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      label: 'En stock',
      value: stats?.en_stock || 0,
      icon: Package,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50'
    },
    {
      label: 'Vendus',
      value: stats?.vendus || 0,
      icon: TrendingUp,
      color: 'text-slate-600',
      bg: 'bg-slate-50'
    },
    {
      label: 'Valeur ventes',
      value: `${(stats?.valeur_ventes || 0).toLocaleString('fr-FR')} €`,
      icon: DollarSign,
      color: 'text-green-600',
      bg: 'bg-green-50'
    }
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}}>Tableau de bord</h2>
          <p className="text-slate-600 mt-2">Vue d'ensemble de votre activité</p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/catalogue"
            target="_blank"
            data-testid="view-catalogue-btn"
            className="flex items-center gap-2 px-4 py-2 bg-white text-slate-900 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
          >
            <ExternalLink size={18} />
            <span>Voir le catalogue</span>
          </Link>
          <button
            onClick={copyCatalogueLink}
            data-testid="copy-catalogue-link-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Copy size={18} />
            <span>Copier le lien</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`} className="stat-card p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-slate-500 text-sm font-medium">{card.label}</span>
                <div className={`${card.bg} p-2 rounded-lg`}>
                  <Icon className={card.color} size={20} />
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-900 font-mono">{card.value}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Véhicules récents</h3>
        </div>
        <div className="p-6">
          {recentVehicles.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500">Aucun véhicule pour le moment</p>
              <Link to="/vehicles/new" data-testid="add-first-vehicle-btn" className="inline-block mt-4 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors">
                Ajouter votre premier véhicule
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentVehicles.map(vehicle => (
                <Link
                  key={vehicle.id}
                  to={`/vehicles/${vehicle.id}`}
                  data-testid={`recent-vehicle-${vehicle.immatriculation}`}
                  className="block p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-slate-900">{vehicle.marque} {vehicle.modele}</p>
                      <p className="text-sm text-slate-600 font-mono">{vehicle.immatriculation}</p>
                    </div>
                    <div className="text-right">
                      <span className={`status-badge status-${vehicle.statut}`}>
                        {vehicle.statut === 'en_stock' ? 'En stock' : vehicle.statut === 'vendu' ? 'Vendu' : 'Réservé'}
                      </span>
                      {vehicle.prix_vente && (
                        <p className="text-sm font-mono text-slate-900 mt-1">{vehicle.prix_vente.toLocaleString('fr-FR')} €</p>
                      )}
                      {vehicle.views > 0 && (
                        <p className="text-xs text-slate-500 mt-1">{vehicle.views} vue(s)</p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
